import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  confirmBankBalance,
  fetchCashForecast,
  proposeBankBalance,
} from '../../api/cashForecast'
import { formatMoney } from '../../lib/formatters'
import { Button } from '../../components/ui/button'

function usd(cents: number) {
  return formatMoney(cents / 100)
}

export default function CashForecastScreen() {
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['cash-forecast', 13],
    queryFn: () => fetchCashForecast(13),
  })
  const [bankInput, setBankInput] = useState('')
  const [pendingConfirm, setPendingConfirm] = useState<{ id: string; summary: string } | null>(null)
  const [bankBusy, setBankBusy] = useState(false)

  if (isLoading) {
    return <div className="p-4 text-sm text-muted-foreground" data-testid="cash-forecast-loading">Loading forecast…</div>
  }
  if (error || !data) {
    return <div className="p-4 text-sm text-destructive">Could not load cash forecast.</div>
  }

  const low = data.lowest_week?.week_start ?? null
  const maxAbs = Math.max(...data.weeks.map((w) => Math.abs(w.position_cents ?? 0)), 1)

  async function onProposeBank() {
    const n = Number(String(bankInput).replace(/[$,]/g, ''))
    if (!Number.isFinite(n) || n <= 0) return
    setBankBusy(true)
    try {
      const res = await proposeBankBalance(n)
      setPendingConfirm({
        id: res.data.confirmation_id,
        summary: res.data.summary,
      })
    } finally {
      setBankBusy(false)
    }
  }

  async function onConfirmBank() {
    if (!pendingConfirm) return
    setBankBusy(true)
    try {
      await confirmBankBalance(pendingConfirm.id)
      setPendingConfirm(null)
      setBankInput('')
      await qc.invalidateQueries({ queryKey: ['cash-forecast'] })
    } finally {
      setBankBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-3xl p-4 space-y-6" data-testid="cash-forecast-page">
      <div>
        <h1 className="text-xl font-semibold">Cash forecast</h1>
        <p className="text-sm text-muted-foreground mt-1">13-week view · computed on load, not stored</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-lg border p-3">
          <div className="text-xs text-muted-foreground">In the bank</div>
          <div className="text-lg font-semibold">
            {data.opening_cents == null ? '—' : usd(data.opening_cents)}
          </div>
          {data.bank_as_of && <div className="text-xs text-muted-foreground">as of {data.bank_as_of}</div>}
        </div>
        <div className="rounded-lg border p-3">
          <div className="text-xs text-muted-foreground">Lowest week</div>
          <div className="text-lg font-semibold text-red-700">
            {data.lowest_week?.position_cents == null ? '—' : usd(data.lowest_week.position_cents)}
          </div>
          <div className="text-xs text-muted-foreground">{low ?? 'Add a bank balance to forecast'}</div>
        </div>
      </div>

      <div className="rounded-lg border p-4 space-y-3" data-testid="cash-forecast-bank-update">
        <p className="text-sm font-medium">Update bank balance (owner)</p>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            inputMode="decimal"
            placeholder="e.g. 340000"
            className="flex-1 min-w-[140px] rounded-md border px-3 py-2 text-sm"
            value={bankInput}
            onChange={(e) => setBankInput(e.target.value)}
          />
          <Button type="button" disabled={bankBusy} onClick={() => void onProposeBank()}>
            Preview
          </Button>
        </div>
        {pendingConfirm && (
          <div className="rounded-md bg-muted/40 p-3 text-sm space-y-2">
            <p>{pendingConfirm.summary}</p>
            <Button type="button" disabled={bankBusy} onClick={() => void onConfirmBank()}>
              Confirm
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-lg border p-4 bg-muted/30">
        <p className="text-sm font-medium">Suggested action</p>
        <p className="text-sm text-muted-foreground mt-1">{data.suggested_action}</p>
      </div>

      <div>
        <div className="text-sm font-medium mb-2">Position by week</div>
        <div className="flex items-end gap-1 h-24" data-testid="cash-forecast-chart">
          {data.weeks.map((w) => {
            const pos = w.position_cents ?? 0
            const h = Math.max(6, Math.round((Math.abs(pos) / maxAbs) * 72))
            const isLow = low != null && w.week_start === low
            const bg = isLow ? 'bg-red-600' : w.provisional ? 'bg-gray-300' : 'bg-blue-700'
            return (
              <div key={w.week_start} className="flex-1 flex flex-col items-center justify-end min-w-0">
                <div className={`w-full max-w-[20px] rounded-t ${bg}`} style={{ height: h }} title={w.week_start} />
              </div>
            )
          })}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border p-4" data-testid="cash-forecast-expected-in">
          <h2 className="text-sm font-semibold mb-2">Expected in</h2>
          <ul className="text-xs space-y-2 text-muted-foreground">
            {(data.expected_in?.open_pay_apps || []).map((row, i) => (
              <li key={`pa-${i}`}>Open PA · {String(row.project_name)} · {usd(Number(row.cents) || 0)}</li>
            ))}
            {(data.expected_in?.scheduled_billings || []).slice(0, 6).map((row, i) => (
              <li key={`sch-${i}`}>
                Scheduled · {String(row.project_name)} · {usd(Number(row.in_cents) || 0)}
                {row.retention_listed_cents ? ` (retention listed ${usd(Number(row.retention_listed_cents))})` : ''}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border p-4" data-testid="cash-forecast-expected-out">
          <h2 className="text-sm font-semibold mb-2">Expected out</h2>
          <ul className="text-xs space-y-1 text-muted-foreground">
            {(data.expected_out?.weekly_lines || []).map((line) => (
              <li key={line.key}>{line.label}: {usd(line.cents_per_week)}/wk</li>
            ))}
            {(data.expected_out?.recurring || []).map((r) => (
              <li key={r.name}>Recurring {r.name}: {usd(r.amount_cents)}/mo (day {r.day_of_month})</li>
            ))}
          </ul>
        </div>
      </div>

      {data.slow_payers?.length ? (
        <div data-testid="cash-forecast-slow-payers">
          <div className="text-sm font-medium mb-2">Who pays late</div>
          <ul className="text-sm space-y-2">
            {data.slow_payers.map((s, i) => (
              <li key={i} className="flex justify-between gap-2 border-b pb-2">
                <span>{s.project_name}</span>
                <span className="text-muted-foreground">{s.days_late}d late · {formatMoney(s.open_balance)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <p className="text-sm">
        <Link to="/dashboard" className="text-primary underline">Back to dashboard</Link>
      </p>
    </div>
  )
}
